import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import { api } from "../lib/api";
import { ShopHeader } from "../components/site/ShopHeader";
import { useAuth } from "../contexts/AuthContext";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  shipped: "bg-blue-100 text-blue-800",
  delivered: "bg-[#ECE4D6] text-[#1F5A34]",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (user === null) return <div className="min-h-screen bg-[#F6F5F0]" />;
  if (user === false)
    return (
      <div className="min-h-screen bg-[#F6F5F0]">
        <ShopHeader />
        <div className="max-w-lg mx-auto text-center py-24 px-6">
          <h1 className="font-display font-bold text-3xl text-[#1A1C19]">Please log in</h1>
          <Link to="/login?next=/account" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#1F5A34] text-[#F6F5F0] font-semibold">Log in</Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F6F5F0]">
      <ShopHeader />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
        <h1 className="font-display font-bold text-4xl text-[#1A1C19]">Hi, {user.name?.split(" ")[0]}</h1>
        <p className="text-[#4A4D48] mt-1">{user.email}</p>

        <h2 className="mt-10 font-display font-bold text-2xl text-[#1A1C19]">Order history</h2>
        {orders === null && <p className="mt-6 text-[#4A4D48]">Loading…</p>}
        {orders && orders.length === 0 && (
          <div className="mt-6 rounded-2xl bg-white border border-[#D1CFC8] p-12 text-center text-[#4A4D48]" data-testid="orders-empty">
            <Package className="w-10 h-10 mx-auto mb-3" /> No orders yet.
            <div><Link to="/shop" className="mt-4 inline-block text-[#1F5A34] font-semibold">Start shopping →</Link></div>
          </div>
        )}
        <div className="mt-6 space-y-4" data-testid="orders-list">
          {orders?.map((o) => (
            <div key={o.id} className="rounded-2xl bg-white border border-[#D1CFC8] p-6" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono text-xs text-[#4A4D48]">#{o.id.slice(0, 8)}</p>
                  <p className="text-sm text-[#4A4D48]">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-700"}`}>{o.status}</span>
              </div>
              <div className="mt-3 divide-y divide-[#ECE4D6]">
                {o.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between py-2 text-sm">
                    <span className="text-[#1A1C19]">{it.name} × {it.qty}</span>
                    <span className="text-[#4A4D48]">${(it.price * it.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-between font-semibold text-[#1A1C19]">
                <span>Total</span><span>${o.total.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
